import React, { useEffect, useRef, useState } from 'react';
import { Camera, ExternalLink, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import { fileToBase64, validateEntryPhoto } from '../../components/EntryPhotoPicker';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { entryPhotoStrings as strings } from '../../content/entryPhotos';
import {
  GET_ENTRY_PHOTO_ALBUMS,
  GET_ENTRY_HIGHLIGHT_PHOTOS,
  GET_PHOTO_STORAGE_USAGE,
  ADD_ENTRY_HIGHLIGHT_PHOTO,
  REMOVE_ENTRY_HIGHLIGHT_PHOTO,
  PROMOTE_ENTRY_PHOTO_TO_COVER,
  REMOVE_ENTRY_COVER_PHOTO,
  REMOVE_ENTRY_PHOTO_ALBUM_LINK,
  SAVE_ENTRY_PHOTO_ALBUM_LINK,
} from './operations';
import './photoAlbums.css';

const emptyDraft = { id: '', label: '', url: '', visibility: 'owner' };

/** Displays provider-hosted albums without importing their photos into Hobtalk. */
export default function PhotoAlbumsSection({ hobbyId, entryId, isOwner = true, canManage = isOwner, editor = false, coverPhotoUrl = '', onCoverChanged, onLimitReached }) {
  const [draft, setDraft] = useState(null);
  const [sharePhoto, setSharePhoto] = useState(false);
  const fileRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentCoverUrl, setCurrentCoverUrl] = useState(coverPhotoUrl);
  const [isUploadingBatch, setIsUploadingBatch] = useState(false);
  useEffect(() => setCurrentCoverUrl(coverPhotoUrl), [coverPhotoUrl]);
  const { data, loading, error, refetch } = useQuery(GET_ENTRY_PHOTO_ALBUMS, {
    variables: { hobbyId, entryId },
    skip: !entryId,
    fetchPolicy: 'cache-and-network',
  });
  const [saveLink, { loading: saving }] = useMutation(SAVE_ENTRY_PHOTO_ALBUM_LINK);
  const [removeLink, { loading: removing }] = useMutation(REMOVE_ENTRY_PHOTO_ALBUM_LINK);
  const { data: highlightData, refetch: refetchHighlights } = useQuery(GET_ENTRY_HIGHLIGHT_PHOTOS, {
    variables: { hobbyId, entryId }, skip: !entryId, fetchPolicy: 'cache-and-network',
  });
  const { data: usageData, refetch: refetchUsage } = useQuery(GET_PHOTO_STORAGE_USAGE, { skip: !canManage });
  const [addHighlight, { loading: uploading }] = useMutation(ADD_ENTRY_HIGHLIGHT_PHOTO);
  const [removeHighlight] = useMutation(REMOVE_ENTRY_HIGHLIGHT_PHOTO);
  const [promotePhoto, { loading: promoting }] = useMutation(PROMOTE_ENTRY_PHOTO_TO_COVER);
  const [removeCover] = useMutation(REMOVE_ENTRY_COVER_PHOTO);
  const links = data?.getEntryPhotoAlbums || [];
  const highlights = highlightData?.getEntryHighlightPhotos || [];
  const usage = usageData?.getPhotoStorageUsage;

  const uploadPhoto = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (files.length + highlights.length > 4) { setErrorMessage(strings.maxPhotosError); return; }
    const validation = files.map(validateEntryPhoto).find(Boolean);
    if (validation) { setErrorMessage(validation); return; }
    setErrorMessage('');
    setIsUploadingBatch(true);
    try {
      let nextCover = currentCoverUrl;
      for (const file of files) {
        const result = await addHighlight({ variables: { input: { hobbyId, entryId, contentType: file.type, base64Data: await fileToBase64(file), visibility: sharePhoto ? 'shared' : 'owner' } } });
        if (!nextCover) {
          const photoId = result.data?.addEntryHighlightPhoto?.id;
          const promoted = await promotePhoto({ variables: { hobbyId, entryId, photoId } });
          nextCover = promoted.data?.promoteEntryPhotoToCover || '';
          setCurrentCoverUrl(nextCover);
          onCoverChanged?.(nextCover);
        }
      }
      await Promise.all([refetchHighlights(), refetchUsage()]);
    } catch (uploadError) {
      setErrorMessage(getPublicErrorMessage(uploadError));
      if (isLimitReachedError(uploadError)) onLimitReached?.();
    } finally {
      setIsUploadingBatch(false);
    }
  };

  const setCover = async (photo) => {
    setErrorMessage('');
    try {
      const result = await promotePhoto({ variables: { hobbyId, entryId, photoId: photo.id } });
      setCurrentCoverUrl(result.data?.promoteEntryPhotoToCover || '');
      onCoverChanged?.(result.data?.promoteEntryPhotoToCover || '');
      await refetchHighlights();
    } catch (cause) { setErrorMessage(getPublicErrorMessage(cause)); }
  };

  const deleteCover = async () => {
    if (!window.confirm(strings.removeCoverPrompt)) return;
    setErrorMessage('');
    try {
      await removeCover({ variables: { hobbyId, entryId } });
      setCurrentCoverUrl('');
      onCoverChanged?.('');
      await refetchUsage();
    } catch (cause) { setErrorMessage(getPublicErrorMessage(cause)); }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm(strings.removePhotoPrompt)) return;
    setErrorMessage('');
    try {
      await removeHighlight({ variables: { hobbyId, entryId, id: photo.id } });
      await Promise.all([refetchHighlights(), refetchUsage()]);
    } catch (removeError) { setErrorMessage(getPublicErrorMessage(removeError)); }
  };

  const save = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    try {
      await saveLink({ variables: { input: { hobbyId, entryId, ...draft, id: draft.id || null } } });
      setDraft(null);
      await refetch();
    } catch (saveError) {
      setErrorMessage(getPublicErrorMessage(saveError));
    }
  };

  const remove = async (link) => {
    if (!window.confirm(strings.removeAlbumPrompt(link.label))) return;
    setErrorMessage('');
    try {
      await removeLink({ variables: { hobbyId, entryId, id: link.id } });
      await refetch();
    } catch (removeError) {
      setErrorMessage(getPublicErrorMessage(removeError));
    }
  };

  if (!canManage && !loading && !error && links.length === 0 && highlights.length === 0) return null;

  return (
    <section className={editor ? 'photo-albums-section' : 'recipe-detail-panel photo-albums-section'}>
      {(canManage || highlights.length > 0) && <>
        <div className="photo-albums-heading">
          <h3>{canManage ? strings.photos : strings.highlights}</h3>
          {canManage && highlights.length < 4 && <button type="button" className="photo-albums-icon-button" title={strings.addPhotos} aria-label={strings.addPhotos} disabled={uploading || isUploadingBatch} onClick={() => fileRef.current?.click()}><Camera size={18} /></button>}
        </div>
        {canManage && <small className="photo-storage-usage">{strings.photoCount(highlights.length + (currentCoverUrl ? 1 : 0))}</small>}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={uploadPhoto} />
        {(highlights.length > 0 || (canManage && currentCoverUrl)) && <div className="entry-highlights-grid">
          {canManage && currentCoverUrl && <div className="entry-highlight"><a href={currentCoverUrl} target="_blank" rel="noopener noreferrer" aria-label="Open cover photo"><img src={currentCoverUrl} alt="Cover" /></a><span className="entry-photo-cover-label">{strings.cover}</span><button type="button" className="photo-albums-icon-button danger" title={strings.removeCover} aria-label={strings.removeCover} onClick={deleteCover}><Trash2 size={16} /></button></div>}
          {highlights.map((photo) => <div className="entry-highlight" key={photo.id}>
            <a href={photo.url} target="_blank" rel="noopener noreferrer" aria-label="Open photo"><img src={photo.url} alt="Trip or hike additional photo" /></a>
            {canManage && <button type="button" className="photo-albums-icon-button danger" title={strings.removeHighlight} aria-label={strings.removeHighlight} onClick={() => deletePhoto(photo)}><Trash2 size={16} /></button>}
            {canManage && <button type="button" className="entry-photo-cover-action" disabled={promoting} onClick={() => setCover(photo)}>{strings.setAsCover}</button>}
          </div>)}
        </div>}
        {canManage && hobbyId === 'traveling' && <label className="photo-album-check"><input type="checkbox" checked={sharePhoto} onChange={(event) => setSharePhoto(event.target.checked)} />{strings.shareHighlights}</label>}
        {canManage && usage && usage.limitBytes > 0 && usage.usedBytes / usage.limitBytes >= 0.9 && <small className="photo-storage-usage">{usage.usedBytes >= usage.limitBytes ? strings.storageFull : strings.storageNearlyFull}</small>}
      </>}
      <div className="photo-albums-heading">
        <h3>{strings.albums}</h3>
        {canManage && !draft && links.length < 3 && (
          <button type="button" className="photo-albums-icon-button" title={strings.addAlbumLink} aria-label={strings.addAlbumLink} onClick={() => setDraft(emptyDraft)}><Plus size={18} /></button>
        )}
      </div>
      {loading && !data && <p className="detail-notes">Loading albums...</p>}
      {error && <p className="form-error">{getPublicErrorMessage(error)}</p>}
      {error && <button type="button" className="btn-secondary" onClick={() => refetch()}>{strings.retryPhotos}</button>}
      {!loading && !error && links.length === 0 && isOwner && <p className="detail-notes">{strings.emptyAlbums}</p>}
      {links.map((link) => (
        <div className="photo-album-row" key={link.id}>
          <Link2 size={18} aria-hidden="true" />
          <a href={link.url} target="_blank" rel="noopener noreferrer" title={`Open ${link.label}`}>
            <strong>{link.label}</strong><small>{link.provider.replaceAll('_', ' ')}</small>
          </a>
          <ExternalLink size={16} aria-hidden="true" />
          {canManage && <>
            <button type="button" className="photo-albums-icon-button" title={`Edit ${link.label}`} aria-label={`Edit ${link.label}`} onClick={() => setDraft({ id: link.id, label: link.label, url: link.url, visibility: link.visibility })}><Pencil size={16} /></button>
            <button type="button" className="photo-albums-icon-button danger" title={`Remove ${link.label}`} aria-label={`Remove ${link.label}`} disabled={removing} onClick={() => remove(link)}><Trash2 size={16} /></button>
          </>}
        </div>
      ))}
      {draft && <div className="photo-album-form">
        <label>{strings.albumName}<input required maxLength={80} value={draft.label} onChange={(event) => setDraft({ ...draft, label: event.target.value })} placeholder="Trip photos" /></label>
        <label>{strings.albumLink}<input required type="url" value={draft.url} onChange={(event) => setDraft({ ...draft, url: event.target.value })} placeholder="https://" /></label>
        {hobbyId === 'traveling' && <label className="photo-album-check"><input type="checkbox" checked={draft.visibility === 'shared'} onChange={(event) => setDraft({ ...draft, visibility: event.target.checked ? 'shared' : 'owner' })} />{strings.sharedAlbum}</label>}
        <small className="photo-storage-usage">{strings.albumStorageNote}</small>
        <div className="photo-album-actions"><button type="button" className="btn-ghost" onClick={() => setDraft(null)}>Cancel</button><button type="button" className="btn-primary" disabled={saving || !draft.label.trim() || !draft.url.trim()} onClick={save}>{saving ? 'Saving...' : strings.saveAlbum}</button></div>
      </div>}
      {errorMessage && <p className="form-error" role="alert">{errorMessage}</p>}
    </section>
  );
}
